import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { uploadDreamcastMediaFile } from "DreamCast/api/media";
import Dropdown from "shared/@forms/Dropdown";
import styles from "./FileUploadSectionV2.module.css";

const BYTES_IN_MB = 1024 * 1024;

const getAcceptedExtensions = (fileType) => {
  if (fileType === "Video") {
    return ".mp4,.mov,.avi,.mkv";
  }

  return ".mp3,.wav,.aac,.m4a";
};

const isAcceptedFile = (file, fileType) => {
  const name = String(file?.name || "").toLowerCase();
  return getAcceptedExtensions(fileType)
    .split(",")
    .some((ext) => name.endsWith(ext));
};

const getSupportedText = (fileType) => {
  if (fileType === "Video") {
    return "Supported: .mp4, .mov, .avi, .mkv";
  }

  return "Supported: .mp3, .wav, .aac, .m4a";
};

const getDropText = (fileType) => {
  if (fileType === "Video") {
    return "Drag & drop your video file here";
  }

  return "Drag & drop your audio file here";
};

const getInitialFileType = (defaultFileType, fileTypeOptions) => {
  if (defaultFileType) {
    return defaultFileType;
  }

  if (fileTypeOptions.includes("Audio")) {
    return "Audio";
  }

  return fileTypeOptions[0] || "";
};

const formatSize = (sizeInBytes) => {
  const mb = sizeInBytes / BYTES_IN_MB;
  return `${mb.toFixed(1)} MB`;
};

const formatFileDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    // Handle ISO format dates (e.g., "2026-07-23T20:48:20")
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) {
      return dateStr;
    }
    // Format as mm/dd/yyyy
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  } catch {
    return dateStr;
  }
};

// Reads a media file's duration in the browser from its local blob URL —
// only the container header is parsed, so this is instant even for multi-GB
// video. Used when the server's metadata probe comes back without a duration
// so a 0:00 never gets saved.
export const measureLocalMediaDuration = (url, kind, timeoutMs = 15000) =>
  new Promise((resolve) => {
    if (!url || typeof document === "undefined") {
      resolve(null);
      return;
    }
    const element = document.createElement(
      kind === "Video" ? "video" : "audio",
    );
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      element.removeAttribute("src");
      element.load?.();
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    element.preload = "metadata";
    element.muted = true;
    element.onloadedmetadata = () => {
      const duration = Number(element.duration);
      finish(Number.isFinite(duration) && duration > 0 ? duration : null);
    };
    element.onerror = () => finish(null);
    element.src = url;
  });

// Label for the server-side phase after the browser finished sending bytes
// (the API reports the background transfer to Bunny as it goes).
export const describeServerStatus = (serverStatus) => {
  const phase = serverStatus?.phase;
  const retry =
    serverStatus?.attempt > 1 ? ` (retry ${serverStatus.attempt - 1})` : "";
  if (phase === "transfer" && serverStatus.fraction != null) {
    const percent = Math.min(99, Math.round(serverStatus.fraction * 100));
    return `Sending to storage... ${percent}%${retry}`;
  }
  if (phase === "retrying") return "Connection to storage dropped, retrying...";
  if (phase === "converting") return "Converting audio...";
  if (phase === "metadata") return "Reading file details...";
  return "Finalizing upload... this can take a while for large files";
};

const formatDuration = (seconds) => {
  // If duration is 0, empty string, null, undefined, or invalid, return "0:00"
  const dur = Number(seconds);
  if (!seconds || Number.isNaN(dur) || dur <= 0) {
    return "0:00";
  }
  const minutes = Math.floor(dur / 60);
  const secs = dur % 60;
  const secsInteger = Math.floor(secs);
  const secsDecimal = (secs % 1).toFixed(2).slice(1); // Get ".XX" format
  return `${minutes}:${String(secsInteger).padStart(2, "0")}${secsDecimal}`;
};

const getFileListSignature = (files) =>
  JSON.stringify(
    (Array.isArray(files) ? files : []).map((file) => ({
      id: file?.id || null,
      type: file?.type || null,
      version: file?.version || null,
      date: file?.date || null,
      name: file?.name || null,
      size: file?.size || null,
      duration: file?.duration || null,
      previewUrl: file?.previewUrl || null,
      localPreviewUrl: file?.localPreviewUrl || null,
      tempFilename: file?.tempFilename || null,
      naCrNumber: file?.naCrNumber || null,
      anzCrNumber: file?.anzCrNumber || null,
    })),
  );

const getPlayablePreviewUrl = (file) =>
  file?.localPreviewUrl || file?.previewUrl || "";

const PLAYBACK_ERROR_MESSAGE = "Couldn't play this file.";

// pause() during a pending play() rejects with AbortError; that's an
// interruption (e.g. switching tracks), not a playback failure.
const isPlaybackInterruption = (error) => error?.name === "AbortError";

const isPlayableFileType = (file) => ["Audio", "Video"].includes(file?.type);

const isPngFile = (fileOrName) => {
  if (!fileOrName) return false;

  const rawName =
    typeof fileOrName === "string"
      ? fileOrName
      : fileOrName.name || fileOrName.tempFilename || fileOrName.id || "";

  return String(rawName).toLowerCase().endsWith(".png");
};

const AudioTypeIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
  </svg>
);

const VideoTypeIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 28 28" aria-hidden="true">
    <circle cx="14" cy="14" r="14" />
    <polygon points="11,8 21,14 11,20" />
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 28 28" aria-hidden="true">
    <circle cx="14" cy="14" r="14" />
    <rect x="10" y="8" width="3.5" height="12" rx="1" />
    <rect x="14.5" y="8" width="3.5" height="12" rx="1" />
  </svg>
);

const TypeIcon = ({ fileType }) => (
  <span className={styles.typeIcon}>
    {fileType === "Video" ? <VideoTypeIcon /> : <AudioTypeIcon />}
  </span>
);

TypeIcon.propTypes = {
  fileType: PropTypes.string.isRequired,
};

export function FileUploadSectionV2({
  label,
  onSave,
  onCancel,
  fileTypeOptions,
  defaultFileType,
  defaultId,
  defaultVersion,
  showManagedFiles,
  initialFiles,
  initialArchivedFiles,
  onFilesChange,
  onArchivedFileRemove,
  uploadFile,
  onUploadStart,
  onUploadEnd,
  required,
  hasError,
  sectionRef,
  onFileTypeChange,
}) {
  const [fileType, setFileType] = useState(() =>
    getInitialFileType(defaultFileType, fileTypeOptions),
  );
  const [activeUpload, setActiveUpload] = useState(null);
  const [isAddingNewFile, setIsAddingNewFile] = useState(false);
  const [managedFiles, setManagedFiles] = useState(initialFiles);
  const [managedArchivedFiles, setManagedArchivedFiles] =
    useState(initialArchivedFiles);
  const [playingFileId, setPlayingFileId] = useState(null);
  // Video previews play in the visible player only; this tracks which file's
  // player is open (it stays open while paused).
  const [previewVideoId, setPreviewVideoId] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [playbackErrorFileId, setPlaybackErrorFileId] = useState(null);

  // Update file type when defaultFileType changes (e.g., when Media Type changes)
  useEffect(() => {
    const newFileType = getInitialFileType(defaultFileType, fileTypeOptions);
    setFileType(newFileType);
  }, [defaultFileType, fileTypeOptions]);

  const fileInputRef = useRef(null);
  const uploadTimerRef = useRef(null);
  const audioPreviewRef = useRef(null);
  const audioPreviewFileIdRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const previewUrlsRef = useRef(new Set());
  const completedUploadIdsRef = useRef(new Set());
  const uploadAbortControllerRef = useRef(null);
  const uploadCancelledRef = useRef(false);
  const managedFilesSignatureRef = useRef(getFileListSignature(initialFiles));
  const managedArchivedSignatureRef = useRef(
    getFileListSignature(initialArchivedFiles),
  );
  const lastEmittedCombinedSignatureRef = useRef(null);
  const hasEmittedInitialManagedStateRef = useRef(false);

  const acceptedExtensions = useMemo(
    () => getAcceptedExtensions(fileType),
    [fileType],
  );
  const supportedText = useMemo(() => getSupportedText(fileType), [fileType]);
  const dropText = useMemo(() => getDropText(fileType), [fileType]);
  const canUpload = !!fileType;
  const renderableManagedFiles = useMemo(
    () => managedFiles.filter((file) => !isPngFile(file)),
    [managedFiles],
  );
  const renderableArchivedFiles = useMemo(
    () => managedArchivedFiles.filter((file) => !isPngFile(file)),
    [managedArchivedFiles],
  );

  const previewVideoFile = useMemo(() => {
    if (!previewVideoId) return null;
    const file =
      renderableManagedFiles.find((f) => f.id === previewVideoId) ||
      renderableArchivedFiles.find((f) => f.id === previewVideoId);
    return file && getPlayablePreviewUrl(file) && file.type === "Video"
      ? file
      : null;
  }, [previewVideoId, renderableManagedFiles, renderableArchivedFiles]);

  useEffect(
    () => () => {
      if (uploadTimerRef.current) {
        window.clearInterval(uploadTimerRef.current);
      }

      previewUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      previewUrlsRef.current.clear();
    },
    [],
  );

  useEffect(() => {
    const nextSignature = getFileListSignature(initialFiles);
    if (nextSignature !== managedFilesSignatureRef.current) {
      managedFilesSignatureRef.current = nextSignature;
      // eslint-disable-next-line no-console
      console.log("[FileUploadSectionV2] initialFiles changed:", initialFiles);
      setManagedFiles(Array.isArray(initialFiles) ? initialFiles : []);
    }
  }, [initialFiles]);

  useEffect(() => {
    const nextSignature = getFileListSignature(initialArchivedFiles);
    if (nextSignature !== managedArchivedSignatureRef.current) {
      managedArchivedSignatureRef.current = nextSignature;
      setManagedArchivedFiles(
        Array.isArray(initialArchivedFiles) ? initialArchivedFiles : [],
      );
    }
  }, [initialArchivedFiles]);

  useEffect(() => {
    managedFilesSignatureRef.current = getFileListSignature(managedFiles);
  }, [managedFiles]);

  useEffect(() => {
    managedArchivedSignatureRef.current =
      getFileListSignature(managedArchivedFiles);
  }, [managedArchivedFiles]);

  useEffect(() => {
    if (showManagedFiles) {
      if (!hasEmittedInitialManagedStateRef.current) {
        hasEmittedInitialManagedStateRef.current = true;
        return;
      }

      const combinedSignature = `${managedFilesSignatureRef.current}::${managedArchivedSignatureRef.current}`;
      if (combinedSignature === lastEmittedCombinedSignatureRef.current) {
        return;
      }

      lastEmittedCombinedSignatureRef.current = combinedSignature;
      onFilesChange({
        files: managedFiles,
        archivedFiles: managedArchivedFiles,
      });
    }
  }, [managedArchivedFiles, managedFiles, onFilesChange, showManagedFiles]);

  const startUpload = async (file) => {
    if (!file || !fileType) {
      return;
    }

    if (isPngFile(file)) {
      setUploadError("PNG files are not supported in Add New File.");
      return;
    }

    if (!isAcceptedFile(file, fileType)) {
      setUploadError(
        `Invalid file type for ${fileType}. ${getSupportedText(fileType)}`,
      );
      return;
    }

    setUploadError(null);
    uploadCancelledRef.current = false;

    if (uploadTimerRef.current) {
      window.clearInterval(uploadTimerRef.current);
    }

    const localPreviewUrl = URL.createObjectURL(file);
    previewUrlsRef.current.add(localPreviewUrl);
    const localDurationPromise = measureLocalMediaDuration(
      localPreviewUrl,
      fileType,
    );

    const uploadId = `${Date.now()}-${file.name}`;
    completedUploadIdsRef.current.delete(uploadId);

    // Create a new AbortController for this upload
    uploadAbortControllerRef.current = new AbortController();

    setActiveUpload({
      id: uploadId,
      name: file.name,
      size: formatSize(file.size || 0),
      progress: 5,
      fileType,
      previewUrl: localPreviewUrl,
    });

    if (onUploadStart) {
      onUploadStart();
    }

    // Real upload progress (bytes sent / file size), reported by the
    // XHR-backed request helper — capped below 100 until the server confirms
    // the upload (metadata extraction/CDN push still needs to finish).
    const handleProgress = (fraction, serverStatus) => {
      if (uploadCancelledRef.current) return;
      const percent = Math.min(99, Math.round(fraction * 100));
      setActiveUpload((current) =>
        current
          ? {
              ...current,
              progress: percent,
              finalizing: percent >= 99,
              serverStatus: serverStatus || current.serverStatus || null,
            }
          : current,
      );
    };

    try {
      const signal = uploadAbortControllerRef.current.signal;
      const response = await (uploadFile
        ? uploadFile(file, signal, handleProgress)
        : uploadDreamcastMediaFile(file, signal, handleProgress));

      if (uploadCancelledRef.current) return;

      setActiveUpload((current) =>
        current ? { ...current, progress: 100 } : current,
      );

      const cdnPreviewUrl = response?.data?.previewUrl || localPreviewUrl;
      const tempFilename = response?.tempFilename || null;
      const storedFilename = tempFilename || file.name;

      if (completedUploadIdsRef.current.has(uploadId)) return;
      completedUploadIdsRef.current.add(uploadId);

      const serverMetadata =
        response?.data?.metadata && typeof response.data.metadata === "object"
          ? response.data.metadata
          : null;
      const serverDuration = Number(serverMetadata?.DurationSeconds);
      const resolvedDuration =
        Number.isFinite(serverDuration) && serverDuration > 0
          ? serverDuration
          : await localDurationPromise;
      const resolvedMetadata =
        serverMetadata || resolvedDuration
          ? {
              ...(serverMetadata || {}),
              DurationSeconds: resolvedDuration || null,
            }
          : null;

      const completedFile = {
        id: defaultId || `${Date.now()}`,
        type: fileType,
        version: String(defaultVersion),
        date: new Date().toLocaleDateString("en-US"),
        name: storedFilename,
        size: formatSize(file.size || 0),
        // The row label reads file.duration; the upload response carries it
        // inside metadata.DurationSeconds.
        duration: resolvedDuration || null,
        previewUrl: cdnPreviewUrl,
        localPreviewUrl,
        tempFilename,
        metadata: resolvedMetadata,
      };

      if (showManagedFiles) {
        // If there's already an active file, archive it and replace with new one
        if (managedFiles.length > 0) {
          setManagedArchivedFiles((archived) => [managedFiles[0], ...archived]);
          setManagedFiles([completedFile]);
        } else {
          // First file upload - just add it
          setManagedFiles([completedFile]);
        }
      }

      onSave({
        fileType: completedFile.type,
        id: completedFile.id,
        version: Number(completedFile.version),
        name: storedFilename,
        size: completedFile.size,
        duration: completedFile.duration,
        previewUrl: completedFile.previewUrl,
        tempFilename,
        metadata: completedFile.metadata,
      });
    } catch (err) {
      if (uploadCancelledRef.current || err.name === "AbortError") {
        // Upload was cancelled, don't show error
        return;
      }
      // eslint-disable-next-line no-console
      console.error(
        "[FileUploadSectionV2] Upload failed:",
        err?.message || err,
      );
      setUploadError(err?.message || "Upload failed. Please try again.");
    } finally {
      setActiveUpload(null);
      setIsAddingNewFile(false);
      if (onUploadEnd) {
        onUploadEnd();
      }
    }
  };

  const handleCancelUpload = () => {
    uploadCancelledRef.current = true;

    if (uploadAbortControllerRef.current) {
      uploadAbortControllerRef.current.abort();
      uploadAbortControllerRef.current = null;
    }

    if (uploadTimerRef.current) {
      window.clearInterval(uploadTimerRef.current);
      uploadTimerRef.current = null;
    }

    if (activeUpload?.previewUrl) {
      URL.revokeObjectURL(activeUpload.previewUrl);
      previewUrlsRef.current.delete(activeUpload.previewUrl);
    }

    setActiveUpload(null);
    setIsAddingNewFile(false);
    if (onUploadEnd) {
      onUploadEnd();
    }
    onCancel();
  };

  const handleAddNewFile = () => {
    if (!canUpload) {
      return;
    }

    setIsAddingNewFile(true);
  };

  const handleBrowse = () => {
    if (!canUpload) {
      return;
    }

    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) {
      return;
    }

    startUpload(selected);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (!canUpload) {
      return;
    }

    const dropped = event.dataTransfer.files?.[0];
    startUpload(dropped);
  };

  const handleArchive = (fileId) => {
    const matched = managedFiles.find((file) => file.id === fileId);
    if (!matched) return;

    const nextFiles = managedFiles.filter((file) => file.id !== fileId);
    const nextArchived = [matched, ...managedArchivedFiles];

    setManagedFiles(nextFiles);
    setManagedArchivedFiles(nextArchived);

    // Push immediately so parent save logic never sees stale active/archive state.
    if (showManagedFiles) {
      onFilesChange({ files: nextFiles, archivedFiles: nextArchived });
    }
  };

  const handleUnarchive = (fileId) => {
    // Find the file to unarchive
    const fileToUnarchive = managedArchivedFiles.find(
      (file) => file.id === fileId,
    );
    if (!fileToUnarchive) return;

    // Calculate new archived list: remove unarchived file, add current active (if any)
    let newArchived = managedArchivedFiles.filter((file) => file.id !== fileId);
    if (managedFiles.length > 0) {
      newArchived = [managedFiles[0], ...newArchived];
    }

    // Update both states synchronously
    const nextFiles = [fileToUnarchive];
    setManagedFiles(nextFiles);
    setManagedArchivedFiles(newArchived);

    // Push immediately so parent save logic never sees stale active/archive state.
    if (showManagedFiles) {
      onFilesChange({ files: nextFiles, archivedFiles: newArchived });
    }
  };

  const handleFileMetadataChange = (fileId, field, value, isArchived = false) => {
    const sourceFiles = isArchived ? managedArchivedFiles : managedFiles;
    const updatedFiles = sourceFiles.map((file) =>
      file.id === fileId ? { ...file, [field]: value } : file,
    );
    const nextFiles = isArchived ? managedFiles : updatedFiles;
    const nextArchivedFiles = isArchived ? updatedFiles : managedArchivedFiles;
    if (isArchived) {
      setManagedArchivedFiles(nextArchivedFiles);
    } else {
      setManagedFiles(nextFiles);
    }

    if (showManagedFiles) {
      onFilesChange({ files: nextFiles, archivedFiles: nextArchivedFiles });
    }
  };

  const handleRemove = (fileId) => {
    const nextFiles = managedFiles.filter((file) => file.id !== fileId);
    setManagedFiles(nextFiles);

    if (showManagedFiles) {
      onFilesChange({ files: nextFiles, archivedFiles: managedArchivedFiles });
    }
  };

  const handleRemoveArchived = (fileId) => {
    setManagedArchivedFiles((current) => {
      const target = current.find((file) => file.id === fileId);
      if (target && onArchivedFileRemove) {
        onArchivedFileRemove(target);
      }
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
        previewUrlsRef.current.delete(target.previewUrl);
      }

      return current.filter((file) => file.id !== fileId);
    });
  };

  const failPlayback = (fileId) => {
    setPlayingFileId(null);
    setPlaybackErrorFileId(fileId);
  };

  const handlePlayRejected = (fileId) => (error) => {
    setPlayingFileId(null);
    if (!isPlaybackInterruption(error)) setPlaybackErrorFileId(fileId);
  };

  const handlePlayToggle = (file) => {
    // eslint-disable-next-line no-console
    console.log("[handlePlayToggle] Toggling file:", file);
    const isAudio = file.type === "Audio";
    const isVideo = file.type === "Video";

    setPlaybackErrorFileId(null);
    const previewUrl = getPlayablePreviewUrl(file);
    if ((!isAudio && !isVideo) || !previewUrl) {
      // eslint-disable-next-line no-console
      console.log(
        "[handlePlayToggle] Play disabled - isAudio:",
        isAudio,
        "isVideo:",
        isVideo,
        "previewUrl:",
        previewUrl,
      );
      if (isAudio || isVideo) setPlaybackErrorFileId(file.id);
      return;
    }

    if (isVideo) {
      audioPreviewRef.current?.pause();
      const player = videoPreviewRef.current;
      if (previewVideoId === file.id && player) {
        if (player.paused) {
          player
            .play()
            .then(() => setPlayingFileId(file.id), handlePlayRejected(file.id));
        } else {
          player.pause();
        }
        return;
      }
      setPreviewVideoId(file.id);
      if (player) {
        player.src = previewUrl;
        player
          .play()
          .then(() => setPlayingFileId(file.id), handlePlayRejected(file.id));
      }
      return;
    }

    const media = audioPreviewRef.current;
    if (!media) {
      setPlaybackErrorFileId(file.id);
      return;
    }

    if (playingFileId === file.id) {
      media.pause();
      setPlayingFileId(null);
      return;
    }

    videoPreviewRef.current?.pause();

    // Prefer local blob URL (no CORS) for freshly uploaded files in the same session
    audioPreviewFileIdRef.current = file.id;
    media.src = previewUrl;
    media
      .play()
      .then(() => {
        setPlayingFileId(file.id);
      })
      .catch((err) => {
        // AbortError is expected when switching tracks; only log unexpected failures
        if (err.name !== "AbortError") {
          // eslint-disable-next-line no-console
          console.error(
            `[MediaPlay] play() rejected:`,
            err.name,
            err.message,
            "src:",
            media.src,
          );
        }
        setPlayingFileId(null);
        if (
          !isPlaybackInterruption(err) &&
          audioPreviewFileIdRef.current === file.id
        ) {
          setPlaybackErrorFileId(file.id);
        }
      });
  };

  const hasManagedFiles =
    renderableManagedFiles.length > 0 || renderableArchivedFiles.length > 0;

  const isRequired = Boolean(required);
  const isInErrorState = Boolean(hasError);

  return (
    <section
      ref={sectionRef}
      className={`${styles.container} ${
        isInErrorState ? styles.errorState : ""
      }`.trim()}
    >
      <input
        ref={fileInputRef}
        type="file"
        className={styles.hiddenInput}
        accept={acceptedExtensions}
        onChange={handleFileInputChange}
      />
      {/* Audio and video elements - render if we have managed files to preview */}
      {(renderableManagedFiles.length > 0 ||
        renderableArchivedFiles.length > 0) && (
        <>
          <audio
            ref={audioPreviewRef}
            className={styles.hiddenInput}
            onEnded={() => setPlayingFileId(null)}
            onPause={() => setPlayingFileId(null)}
            onError={() => {
              // Fires for 403/404s, HTML error pages and undecodable media.
              if (audioPreviewFileIdRef.current) {
                failPlayback(audioPreviewFileIdRef.current);
              }
            }}
          />
        </>
      )}

      <div className={styles.headerSection}>
        <h3 className={styles.heading}>
          {label}
          <span className={styles.requiredIndicator}> *</span>
        </h3>
        <div className={styles.divider} />
      </div>

      <div className={styles.fileTypeRow}>
        <span className={styles.fileTypeLabel}>File Type:</span>
        <Dropdown
          className={styles.fileTypeDropdown}
          value={fileType}
          options={fileTypeOptions}
          placeholder="Select File Type"
          onChange={(nextValue) => {
            setFileType(nextValue);
            setUploadError(null);
            // Notify parent when file type changes so Media Type can be updated
            if (onFileTypeChange) {
              onFileTypeChange(nextValue);
            }
          }}
          chevronClassName={styles.fileTypeChevron}
          chevronOpenClassName={styles.fileTypeChevronUp}
          buttonBackgroundColor="#ffffff"
          menuBackgroundColor="#283b4d"
        />
      </div>

      {!activeUpload &&
        (!showManagedFiles || !hasManagedFiles || isAddingNewFile) && (
          <div
            className={`${styles.dropZone} ${
              isInErrorState ? styles.dropZoneError : ""
            }`.trim()}
            onDrop={handleDrop}
            onDragOver={(event) => event.preventDefault()}
          >
            <TypeIcon fileType={fileType} />
            <span className={styles.dropText}>{dropText}</span>
            <span className={styles.orText}>or</span>
            <button
              type="button"
              className={styles.browseButton}
              onClick={handleBrowse}
              disabled={!canUpload}
            >
              Browse Files
            </button>
            <span className={styles.supportedText}>{supportedText}</span>
            {isRequired && (
              <span className={styles.requiredHint}>Required</span>
            )}
          </div>
        )}

      {uploadError && <p className={styles.uploadError}>{uploadError}</p>}

      {(activeUpload || (showManagedFiles && hasManagedFiles)) && (
        <div className={styles.uploadedList}>
          {activeUpload && (
            <div className={styles.uploadingCard}>
              <div className={styles.uploadedRow}>
                <div className={styles.uploadedFileInfo}>
                  <TypeIcon fileType={activeUpload.fileType} />
                  <div className={styles.uploadingTextBlock}>
                    <span className={styles.uploadingFileName}>
                      {activeUpload.name}
                    </span>
                    <span className={styles.uploadingFileSize}>
                      {activeUpload.size}
                    </span>
                  </div>
                </div>
              </div>

              <div className={styles.progressTrack}>
                <div
                  className={
                    activeUpload.finalizing
                      ? `${styles.progressFill} ${styles.progressFillIndeterminate}`
                      : styles.progressFill
                  }
                  style={
                    activeUpload.finalizing
                      ? undefined
                      : { width: `${activeUpload.progress}%` }
                  }
                />
              </div>
              <div className={styles.uploadingFooter}>
                <span>
                  {activeUpload.finalizing
                    ? describeServerStatus(activeUpload.serverStatus)
                    : `Uploading...${activeUpload.progress}%`}
                </span>
                <button
                  type="button"
                  className={styles.cancelUploadButton}
                  onClick={handleCancelUpload}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {(renderableManagedFiles.some((file) => file.type === "Video") ||
            renderableArchivedFiles.some((file) => file.type === "Video")) && (
            <div
              className={styles.videoPreviewContainer}
              hidden={!previewVideoFile}
            >
              <video
                ref={videoPreviewRef}
                className={styles.videoPreview}
                controls
                src={
                  previewVideoFile
                    ? getPlayablePreviewUrl(previewVideoFile)
                    : undefined
                }
                onPlay={() => {
                  audioPreviewRef.current?.pause();
                  setPlayingFileId(previewVideoFile.id);
                  setPlaybackErrorFileId((current) =>
                    current === previewVideoFile.id ? null : current,
                  );
                }}
                onPause={() => setPlayingFileId(null)}
                onEnded={() => setPlayingFileId(null)}
                onError={() => {
                  if (previewVideoFile) failPlayback(previewVideoFile.id);
                }}
              />
            </div>
          )}

          {showManagedFiles &&
            renderableManagedFiles.map((file) => (
              <div className={styles.uploadedCard} key={file.id}>
                <div className={styles.uploadedRow}>
                  <div className={styles.uploadedFileInfo}>
                    <TypeIcon fileType={file.type} />
                    <div className={styles.uploadingTextBlock}>
                      <span className={styles.uploadingFileName}>
                        {file.name || file.id}
                      </span>
                      <span className={styles.uploadingFileSize}>{`${
                        file.size || "0.00 MB"
                      } · ${formatDuration(file.duration)} · ${formatFileDate(
                        file.date,
                      )}`}</span>
                    </div>
                  </div>

                  <div className={styles.uploadedActions}>
                    <button
                      type="button"
                      className={styles.playButton}
                      aria-label={
                        playingFileId === file.id
                          ? "Pause media preview"
                          : "Play media preview"
                      }
                      onClick={() => handlePlayToggle(file)}
                      disabled={!isPlayableFileType(file)}
                    >
                      {playingFileId === file.id ? <PauseIcon /> : <PlayIcon />}
                    </button>
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => handleRemove(file.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                {playbackErrorFileId === file.id && (
                  <p className={styles.uploadError} role="alert">
                    {PLAYBACK_ERROR_MESSAGE}
                  </p>
                )}

                <div className={styles.metadataRow}>
                  <div className={styles.metadataField}>
                    <label className={styles.metadataLabel}>NA CR Number</label>
                    <input
                      type="text"
                      className={styles.metadataInput}
                      placeholder="Number"
                      value={file.naCrNumber || ""}
                      onChange={(event) =>
                        handleFileMetadataChange(
                          file.id,
                          "naCrNumber",
                          event.target.value,
                        )
                      }
                    />
                  </div>
                  <div className={styles.metadataField}>
                    <label className={styles.metadataLabel}>
                      ANZ CR Number
                    </label>
                    <input
                      type="text"
                      className={styles.metadataInput}
                      placeholder="Number"
                      value={file.anzCrNumber || ""}
                      onChange={(event) =>
                        handleFileMetadataChange(
                          file.id,
                          "anzCrNumber",
                          event.target.value,
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            ))}

          {!activeUpload && !isAddingNewFile && (
            <button
              type="button"
              className={styles.addNewFileButton}
              onClick={handleAddNewFile}
              disabled={!canUpload}
            >
              + Add new file
            </button>
          )}

          {renderableArchivedFiles.length > 0 && (
            <>
              <span className={styles.archiveHeading}>Archive</span>
              {renderableArchivedFiles.map((file) => (
                <div className={styles.uploadedCard} key={file.id}>
                  <div className={styles.uploadedRow}>
                    <div className={styles.uploadedFileInfo}>
                      <TypeIcon fileType={file.type} />
                      <div className={styles.uploadingTextBlock}>
                        <span className={styles.uploadingFileName}>
                          {file.name || file.id}
                        </span>
                        <span className={styles.uploadingFileSize}>{`${
                          file.size || "0.00 MB"
                        } · ${formatDuration(file.duration)} · ${formatFileDate(
                          file.date,
                        )}`}</span>
                      </div>
                    </div>

                    <div className={styles.uploadedActions}>
                      <button
                        type="button"
                        className={styles.playButton}
                        aria-label={
                          playingFileId === file.id
                            ? "Pause media preview"
                            : "Play media preview"
                        }
                        onClick={() => handlePlayToggle(file)}
                        disabled={!isPlayableFileType(file)}
                      >
                        {playingFileId === file.id ? (
                          <PauseIcon />
                        ) : (
                          <PlayIcon />
                        )}
                      </button>
                      <button
                        type="button"
                        className={styles.archiveRemoveButton}
                        onClick={() => handleRemoveArchived(file.id)}
                      >
                        Remove
                      </button>
                      <button
                        type="button"
                        className={styles.archiveUnarchiveButton}
                        onClick={() => handleUnarchive(file.id)}
                      >
                        Unarchive
                      </button>
                    </div>
                  </div>

                  {playbackErrorFileId === file.id && (
                    <p className={styles.uploadError} role="alert">
                      {PLAYBACK_ERROR_MESSAGE}
                    </p>
                  )}

                  <div className={styles.metadataRow}>
                    <div className={styles.metadataField}>
                      <label className={styles.metadataLabel}>
                        NA CR Number
                      </label>
                      <input
                        type="text"
                        className={styles.metadataInput}
                        placeholder="Number"
                        value={file.naCrNumber || ""}
                        onChange={(event) =>
                          handleFileMetadataChange(
                            file.id,
                            "naCrNumber",
                            event.target.value,
                            true,
                          )
                        }
                      />
                    </div>
                    <div className={styles.metadataField}>
                      <label className={styles.metadataLabel}>
                        ANZ CR Number
                      </label>
                      <input
                        type="text"
                        className={styles.metadataInput}
                        placeholder="Number"
                        value={file.anzCrNumber || ""}
                        onChange={(event) =>
                          handleFileMetadataChange(
                            file.id,
                            "anzCrNumber",
                            event.target.value,
                            true,
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </section>
  );
}

FileUploadSectionV2.propTypes = {
  label: PropTypes.string,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  onUploadStart: PropTypes.func,
  onUploadEnd: PropTypes.func,
  fileTypeOptions: PropTypes.arrayOf(PropTypes.string),
  defaultFileType: PropTypes.string,
  defaultId: PropTypes.string,
  defaultVersion: PropTypes.number,
  showManagedFiles: PropTypes.bool,
  uploadFile: PropTypes.func,
  initialFiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      version: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      name: PropTypes.string,
      size: PropTypes.string,
      previewUrl: PropTypes.string,
    }),
  ),
  initialArchivedFiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      version: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      name: PropTypes.string,
      size: PropTypes.string,
      previewUrl: PropTypes.string,
    }),
  ),
  onFilesChange: PropTypes.func,
  onArchivedFileRemove: PropTypes.func,
  onFileTypeChange: PropTypes.func,
  required: PropTypes.bool,
  hasError: PropTypes.bool,
  sectionRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

FileUploadSectionV2.defaultProps = {
  label: "Add New File",
  onSave: () => {},
  onCancel: () => {},
  fileTypeOptions: ["Audio", "Video"],
  defaultFileType: "",
  defaultId: "",
  defaultVersion: 1,
  showManagedFiles: false,
  initialFiles: [],
  initialArchivedFiles: [],
  onFilesChange: () => {},
  onArchivedFileRemove: () => {},
  onFileTypeChange: () => {},
  uploadFile: null,
  required: false,
  hasError: false,
  sectionRef: null,
};

export default FileUploadSectionV2;
